import React, {Component} from 'react';
import Select from 'react-select';
import {Creatable} from 'react-select';
import PropTypes from "prop-types";
import {InputField, MultipleField, ImageField} from '../FieldComponents';
import {getTopLevelOptions, optionTree, createAlertMessage} from '../../lib/AdminFunctions';
import Button from '../Button';
import Api from '../../Api';

import './style.less';
import Processing from "../Processing/index";

const OPTION_GROUP_NAMES = [
  {label: 'default', value: 'default'},
  {label: 'Color', value: 'Color'},
  {label: 'Scent', value: 'Scent'},
  {label: 'Size', value: 'Size'},
  {label: 'Material', value: 'Material'}
];


class OptionField extends Component {
  constructor(props) {
    super(props);
    this.onInput = this.onInput.bind(this);
    this.onImage = this.onImage.bind(this);
  }

  static propTypes = {
    name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.object
    ]),
    onInput: PropTypes.func.isRequired
  };

  onInput(name, value) {
    let option = Api.Utils.clone(this.props.value);
    option[name] = value;
    this.props.onInput(this.props.name, option);
  }

  onImage(_, image) {
    let {name, images, brand_code, ...rest} = this.props.value;
    // Update to just one image
    images = [image];

    let option = {
      name: name || '',
      brand_code: brand_code || '',
      images,
      ...rest
    };
    this.props.onInput(this.props.name, option);
  }

  render() {
    let {name, images, brand_code} = this.props.value;
    let firstImage = (images && images[0]) || {url: ''};
    return (
      <div className="option-field">
        <InputField name="name"
                    required={true}
                    onInput={this.onInput}
                    placeholder="Option Name"
                    value={name || ''}/>
        <InputField name="brand_code"
                    placeholder="Option SKU"
                    onInput={this.onInput}
                    value={brand_code || ''}/>
        <ImageField name="image"
                    placeholder="Option Image"
                    onInput={this.onImage}
                    value={firstImage || ''}/>
      </div>
    );
  }
}

const ErrorClass = "error";

function mapFlatCategoriesToOptions(categoryTree, flatCategories) {
  let categoryNames = Object.keys(categoryTree);
  let categories = flatCategories.filter(category => categoryNames.indexOf(category) >= 0);
  let subcategories = flatCategories.filter(category => categoryNames.indexOf(category) === -1);

  return subcategories.map(subcategory => {
    for (let category of categories) {
      let potentialSubcategories = categoryTree[category];
      if (potentialSubcategories.indexOf(subcategory) >= 0) {
        let option = `${category}: ${subcategory}`;
        return {label: option, value: option};
      }
    }
    return {label: subcategory, value: subcategory};
  });
}

export default class ProductEditor extends Component {
  constructor(props, context) {
    super(props, context);

    this.onImagesChange = this.onImagesChange.bind(this);
    this.onImagesClear = this.onImagesClear.bind(this);
    this.onImagesNew = this.onImagesNew.bind(this);
    this.onInput = this.onInput.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.onOptionChange = this.onOptionChange.bind(this);
    this.onOptionName = this.onOptionName.bind(this);
    this.onOptionClear = this.onOptionClear.bind(this);
    this.onNewOption = this.onNewOption.bind(this);
    this.onShowAdvanced = this.onShowAdvanced.bind(this);
    this.clearSavedView = this.clearSavedView.bind(this);
    this.onProductChanged = this.onProductChanged.bind(this);
    this.onCategoriesChange = this.onCategoriesChange.bind(this);
    this.onCategoryOptionsChanged = this.onCategoryOptionsChanged.bind(this);
    this.onDuplicate = this.onDuplicate.bind(this);
    this.onSaveCommand = this.onSaveCommand.bind(this);
    this.validRetailPrice = this.validRetailPrice.bind(this);
    this.validWholesalePrice = this.validWholesalePrice.bind(this);
    this.state = this.createState(this.props.product);
    this.ctrlCmdKeyDown = false;
  }

  static PropTypes = {
    brandToken: PropTypes.string.isRequired,
    brandName: PropTypes.string,
    token: PropTypes.string,
    product: PropTypes.object.isRequired,
    onUpdated: PropTypes.func,
    onDuplicate: PropTypes.func
  };


  componentWillMount() {
    Api.cache.addChangeListener(this.onProductChanged, Api.cache.eventTypes.PRODUCTS_CHANGED);
    Api.cache.addChangeListener(this.onCategoryOptionsChanged, Api.cache.eventTypes.CATEGORIES_CHANGED);
  }

  componentDidMount() {
    Api.getCategories(true).then(categories => this.setState({categoryTree: categories}));
    window.document.addEventListener('keydown', this.onSaveCommand);
  }

  componentWillUnmount() {
    Api.cache.removeChangeListener(this.onProductChanged, Api.cache.eventTypes.PRODUCTS_CHANGED);
    Api.cache.removeChangeListener(this.onCategoryOptionsChanged, Api.cache.eventTypes.CATEGORIES_CHANGED);
    window.document.removeEventListener('keydown', this.onSaveCommand);
  }

  onSaveCommand(event) {
    const isModified = event.getModifierState('Control') || event.getModifierState('Meta');
    if (event.key === 's' && isModified) {
      event.preventDefault();
      this.handleSubmit(event);
    }
  }

  onProductChanged() {
    if ((!this.props.product || !this.props.product.token) && !this.newToken) return;
    const productToken = (this.props.product && this.props.product.token) || this.newToken;
    Api.getProductCache(productToken)
      .then(result => {
        this.setState(this.createState(result.products[productToken], this.state.showAdvanced, this.saved));
      })
      .catch((error) => {
        console.error('error', error);
      });
  }

  onCategoryOptionsChanged() {
    let categoryTree = Api.cache.getCategories();
    let flatCategories = this.state.product.categories;
    let categories = mapFlatCategoriesToOptions(categoryTree, flatCategories);
    this.setState({
      categoryTree,
      categories
    });
  }

  createState(originalProduct, showAdvanced = false, saved) {
    let product = Api.Utils.clone(originalProduct);
    let {optionName, optionValues} = getTopLevelOptions(product.options);
    optionName = {label: optionName, value: optionName};

    let savedViewCount = saved ? ((this.state && this.state.savedViewCount) || 0) + 1 : 0;
    this.saved = false;

    let categoryTree = Api.cache.getCategories();
    let selectedCategories = [];
    if (categoryTree && product.categories) {
      selectedCategories = mapFlatCategoriesToOptions(categoryTree, product.categories)
    }

    let images = (product.images || []).map(image => {
      return {key: image.token, value: image}
    });

    product.wholesale_price_dollars = Api.Utils.formatCents(product.wholesale_price_cents, false, true) || '';
    product.retail_price_dollars = Api.Utils.formatCents(product.retail_price_cents, false, true) || '';

    return {
      product: product,
      categoryTree,
      categories: selectedCategories,
      error: {
        name: !product.name,
        wholesale_price_cents: !product.wholesale_price_cents
        || (product.retail_price_cents && product.wholesale_price_cents > product.retail_price_cents),
        retail_price_cents: !product.retail_price_cents
        || (product.wholesale_price_cents && product.retail_price_cents < product.wholesale_price_cents),
        unit_multiplier: !product.unit_multiplier
      },
      errorView: null,
      images,
      showAdvanced: showAdvanced,
      optionName,
      optionValues,
      savedView: saved ? this.createSavedView() : null,
      savedViewCount: savedViewCount
    };
  }

  onImagesChange(index, key, value) {
    let images = this.state.images;
    if (index > images.length) {
      images.push({key, value});
    } else {
      images[index] = {key, value};
    }
    this.setState({
      images
    });
  }

  onImagesNew() {
    return {key: `image-${this.state.images.length}-${Date.now()}`, value: {url: ''}}
  }

  onImagesClear(index) {
    let images = this.state.images;
    images.splice(index, 1);
    this.setState({
      images
    });
  }

  onShowAdvanced() {
    this.setState({
      showAdvanced: true
    });
  }

  onInput(name, input, error) {
    let product = this.state.product;
    product[name] = input;

    let errorState = this.state.error || {};
    if (name === 'wholesale_price_dollars' || name === 'retail_price_dollars') {
      const centsName = name.replace('dollars', 'cents');
      product[centsName] = Api.Utils.dollarToCents(input);
      errorState[centsName] = !!error;
    } else {
      errorState[name] = !!error;
    }

    this.setState({
      product,
      error: errorState
    });

    if (this.props.onUpdated) {
      this.props.onUpdated(this.props.token, product);
    }
  }

  onCategoriesChange(selectedCategoryOptions) {
    this.setState({
      categories: selectedCategoryOptions
    });
  }

  onOptionChange(index, optionKey, option) {
    let optionValues = this.state.optionValues;
    optionValues[index] = {key: optionKey, value: option};
    this.setState({
      optionValues
    });
  }

  onOptionClear(index, name, value) {
    if (this.state.optionValues.length === 1) {
      alert('Must have one option');
      return;
    }

    if (window.confirm(`Do you want to delete this option ${name}: ${value.name}`)) {
      let optionValues = this.state.optionValues;

      if (value.token) {
        this.setState({
          deletingOption: true
        });
        Api.deleteProductOption(this.props.product.token, value.token)
          .then(() => {
            this.setState({
              deletingOption: false
            }, () => {
              alert('Option deleted');
            })
          })
          .catch(error => {
            alert('Failed to delete option');
            console.error('error deleting option', error);
            this.setState({
              deletingOption: false
            });
          });
      } else {
        optionValues.splice(index, 1);
        this.setState({
          optionValues
        })
      }
    }
  }

  validWholesalePrice(input) {
    const centsValue = Api.Utils.dollarToCents(input);
    if (centsValue === null) return false;

    if (!this.state.product || !this.state.product.retail_price_cents) return true;
    const valid = centsValue <= this.state.product.retail_price_cents;
    if (valid) {
      this.onInput('retail_price_dollars', this.state.product.retail_price_dollars, false);
    }
    return valid;
  }

  validRetailPrice(input) {
    const centsValue = Api.Utils.dollarToCents(input);
    if (centsValue === null) return false;

    if (!this.state.product || !this.state.product.wholesale_price_cents) return true;
    const valid = centsValue >= this.state.product.wholesale_price_cents;
    if (valid) {
      this.onInput('wholesale_price_dollars', this.state.product.wholesale_price_dollars, false);
    }
    return valid;
  }

  onNewOption() {
    let key = `option-${this.state.optionValues.length}-${Date.now()}`;
    return {key, value: {name: '', brand_code: '', images: []}}
  }

  onOptionName(selected) {
    this.setState({
      optionName: selected
    });
  }

  submit() {
    let product = this.state.product;
    let data = {
      brand_token: this.props.brandToken,
      active: product.active,
      maker_best_seller: product.maker_best_seller,
      name: product.name.trim(),
      wholesale_price_cents: product.wholesale_price_cents,
      retail_price_cents: product.retail_price_cents,
      unit_multiplier: product.unit_multiplier,
      description: product.description,
      amazon_url: product.amazon_url
    };

    if (product && product.token) {
      data.token = product.token;
    }

    if (this.state.categories && this.state.categories.length > 0) {
      data.categories = this.state.categories.reduce((flatCategories, categoryGroup) => {
        return flatCategories.concat(categoryGroup.value.split(':').map(str => str.trim()));
      }, []);
    }

    if (this.state.images.length) {
      data.images = this.state.images
        .map(imageInput => imageInput.value)
        .filter(image => !!image.url);
    }


    let nestedOptions = (product.options || [])
      .filter((o) => {
        return !!o.parent_token
      });
    let modifiedOptions = this.state.optionValues
      .map(o => o.value)
      .filter(o => o && o.name)
      .map((o) => {
        o.group_name = this.state.optionName.value;
        o.unit_multiplier = o.unit_multiplier || product.unit_multiplier;
        o.wholesale_price_cents = o.wholesale_price_cents || product.wholesale_price_cents;
        o.retail_price_cents = o.retail_price_cents || product.retail_price_cents;
        return o;
      });

    data.options = nestedOptions.concat(modifiedOptions);

    return Api.saveProduct(data)
      .then(response => {
        const products = response.products;
        this.newToken = Object.keys(products)[0];
        const product = products[this.newToken];
        (this.props.onUpdated && this.props.onUpdated(this.props.token, product, this.newToken)); // eslint-disable-line no-unused-expressions
        this.saved = true;
      })
      .catch(error => {
          alert(createAlertMessage(error));
          this.setState({
            savedView: null,
            savedViewCount: 0,
            errorView: this.createErrorView(error)
          })
        }
      );
  }

  handleSubmit(event) {
    event.preventDefault();
    if (this.handleError()) {
      return;
    }
    let submitted = this.submit();
    submitted.then(() => this.onProductChanged());
    return submitted;
  }

  handleError() {
    let errorValues = Object.values(this.state.error) || [];
    let error = errorValues
      .some((value) => {
        return !!value
      });
    if (error) {
      this.setState({
        savedView: null,
        savedViewCount: 0,
        errorView: this.createErrorView()
      })
    } else {
      this.setState({
        savedView: null,
        savedViewCount: 0,
        errorView: null
      })
    }

    return error;
  }

  createErrorView(serverError) {
    let view;
    if (serverError) {
      view = JSON.stringify((serverError && serverError.message) || 'There was an error saving the content');
    } else {
      let messages = Object.entries(this.state.error)
        .filter((entry) => {
          return !!entry[1];
        })
        .map((entry) => {
          let name = entry[0];
          if (name === 'wholesale_price_cents' || name === 'retail_price_cents') {
            name = name.replace('_cents', '');
          }
          return `Invalid ${name.replace('_', ' ')} field`;
        });
      let listItems = messages.map((m, index) => {
        let key = `error-message-${index}`;
        return <li key={key}>{m}</li>
      });
      view = <ul>{listItems}</ul>;
    }

    return <div className="admin-error-message">{view}</div>;
  }

  createSavedView() {
    return <div className="saved-box">Saved!</div>;
  }

  clearSavedView() {
    let savedViewCount = Math.max(this.state.savedViewCount - 1, 0);
    let savedView = (savedViewCount === 0) ? null : this.state.savedView;
    if (!savedView) {
      this.saved = false;
    }
    this.setState({
      savedViewCount,
      savedView
    });
  }

  onDuplicate() {
    const duplicate = Api.Utils.clone(this.props.product);
    delete duplicate.token;
    this.props.onDuplicate(duplicate);
  }

  render() {
    if (!this.state.categoryTree) {
      return <Processing />;
    }
    let product = this.state.product;

    let googleImageLink = `https://www.google.com/search?tbm=isch&q=${(product.name) || ''} ${(this.props.brandName) || ''}`;

    let firstProductImageUrl = null;
    let imageUrls = this.state.images.filter(image => !!image.value.url).map(image => image.value.url);
    if (imageUrls && imageUrls.length > 0) {
      firstProductImageUrl = imageUrls[0];
    }

    let productImageView = firstProductImageUrl ? <img role="presentation" src={firstProductImageUrl}/> : null;

    if (this.state.savedView) {
      setTimeout(this.clearSavedView, 2000);
    }

    // assumes every category (non-retailer specific) has subcategories
    let categoryNames = Object.keys(this.state.categoryTree);
    let categoryOptions = [];
    categoryNames.forEach(category => {
      let subcategories = this.state.categoryTree[category];
      subcategories.forEach(subcategory => {
        let option = `${category}: ${subcategory}`;
        categoryOptions.push({label: option, value: option});
      });
    });


    let showAdvanced = this.state.showAdvanced;
    let advancedView;
    if (!showAdvanced && product.token) {
      advancedView = <Button onClick={this.onShowAdvanced}>Show Advanced...</Button>;
    } else if (showAdvanced) {
      let options = optionTree(this.props.brandToken, product);
      advancedView = (
        <div className="options">
          <h3>Options</h3>
          <div>{options}</div>
          <div>
            <Button to={'/admin/brand/' + this.props.brandToken + '/product/' + product.token + '/option/new'}>Add
              Option</Button>
          </div>
        </div>
      );
    } else {
      advancedView = null;
    }

    let deletingOptionView = this.state.deletingOption ? <div>Deleting option...<Processing /></div> : null;

    const duplicateActionView = product.token
      ? <div><Button onClick={this.onDuplicate}>Duplicate</Button></div>
      : null;

    return (
      <div className="product-editor">
        <form className="admin-editor" onSubmit={this.handleSubmit}>
          <h4>{this.state.name}</h4>
          {this.state.errorView}
          <div className="product-editor-image">
            {productImageView}
          </div>
          {duplicateActionView}
          <div className="line">
            <label className="label required">Name</label>
            <InputField name="name"
                        onInput={this.onInput}
                        required={true}
                        type="text"
                        className={(this.state.error && this.state.error.name) ? ErrorClass : ''}
                        value={product.name || ''}
                        placeholder="Name"/>
          </div>

          <div className="line">
            <label className="label">Active</label>
            <InputField type="checkbox"
                        onInput={this.onInput}
                        name="active"
                        defaultChecked={product.active}/>
          </div>

          <div className="line">
            <label className="label">Maker Best Seller</label>
            <InputField type="checkbox"
                        onInput={this.onInput}
                        name="maker_best_seller"
                        defaultChecked={product.maker_best_seller}/>
          </div>

          <div className="line line-block">
            <label className="label">Description</label>
            <InputField type="textarea"
                        name="description"
                        onInput={this.onInput}
                        value={product.description || ''}
                        placeholder="Description"/>
          </div>

          <div className="line">
            <label className="label required">Wholesale Price $</label>
            <InputField name="wholesale_price_dollars"
                        onInput={this.onInput}
                        className={(this.state.error && this.state.error.wholesale_price_cents) ? ErrorClass : ''}
                        required={true}
                        type="tel"
                        value={product.wholesale_price_dollars}
                        validator={this.validWholesalePrice}
                        placeholder="Wholesale Price"/>
          </div>

          <div className="line">
            <label className="label required">Retail Price $</label>
            <InputField name="retail_price_dollars"
                        onInput={this.onInput}
                        required={true}
                        className={(this.state.error && this.state.error.retail_price_cents) ? ErrorClass : ''}
                        type="tel"
                        validator={this.validRetailPrice}
                        value={product.retail_price_dollars}
                        placeholder="Retail Price"/>
          </div>

          <div className="line">
            <label className="label required">Case Quantity</label>
            <InputField name="unit_multiplier"
                        onInput={this.onInput}
                        required={true}
                        className={(this.state.error && this.state.error.unit_multiplier) ? ErrorClass : ''}
                        type="number"
                        value={product.unit_multiplier || ''}
                        placeholder="Case Quantity"/>
          </div>

          <div className="line line-block">
            <label htmlFor="categories" className="label">Categories</label>
            <Select
              name="categories"
              clearable={true}
              value={this.state.categories}
              multi={true}
              options={categoryOptions}
              placeholder="Product Categories"
              onChange={this.onCategoriesChange}
            />
          </div>

          <div className="line">
            <a href={googleImageLink} target="_blank">Search on Google for Product Images</a>
          </div>

          <div className="line line-block">
            <label className="label required">Product Images</label>
            <MultipleField name="images"
                           onClear={this.onImagesClear}
                           onNew={this.onImagesNew}
                           component={ImageField}
                           inputs={this.state.images}
                           onChange={this.onImagesChange}/>
          </div>

          <div className="line">
            <label className="label">Amazon Url</label>
            <InputField name="amazon_url"
                        onInput={this.onInput}
                        type="text"
                        value={product.amazon_url || ''}
                        placeholder="Amazon URL"/>
          </div>

          <hr />

          {deletingOptionView}
          <div className="line">
            <label className="label">Option Group Name</label>
            <Creatable name="option_name"
                       options={OPTION_GROUP_NAMES}
                       clearable={false}
                       onChange={this.onOptionName}
                       type="text"
                       value={this.state.optionName}/>
          </div>

          <div className="line line-block">
            <label className="label">Option Values</label>
            <MultipleField name="optionValues"
                           onClear={this.onOptionClear}
                           disabled={this.state.deletingOption}
                           component={OptionField}
                           inputs={this.state.optionValues}
                           onNew={this.onNewOption}
                           onChange={this.onOptionChange}/>
          </div>
          {this.state.savedView}
          <Button onClick={this.handleSubmit}>Save</Button>
        </form>
        <hr />
        {advancedView}
      </div>);
  }
}




/** WEBPACK FOOTER **
 ** ./src/components/ProductEditor/index.jsx
 **/